import { useCallback, useRef, useState } from 'react';

import { CROWN_RIPS_WIDGET_TEST_ID } from '../../../../../../features/widgets/components/CrownRipsWidget/constants';
import { RIPS_PACK_REWARD_IMAGE_TEST_ID } from '../RipsPackRewardComponent/RipsPackRewardComponent';

import {
  FLY_SAFETY_TIMEOUT_MS,
  PACK_FLY_FALLBACK_TARGET_MARGIN_PX,
  PACK_FLY_HERO_DRIFT_PX,
  PACK_FLY_HOLD_CAP_MS,
} from './constants';

export type PackFlyPoint = { x: number; y: number };

// Deliberately narrower than DOMRect — a plain data shape so callers (incl. tests) don't have to
// fake DOMRect-only members (toJSON, x/y) they never use.
export type PackFlyRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};

// What the claim dialog captures at claim time and hands off (via EventTriggerStore) to the
// persistent host — see captureRipsPackFlySource. Plain data, no timers/phase: safe to store in
// mobx and to survive the dialog that captured it unmounting.
export type RipsPackClaimFlyRequest = {
  imageUrl: string;
  sourceRect: PackFlyRect;
};

export type PackFlyState = RipsPackClaimFlyRequest & {
  // null while holding (phase 1, in place over the claimed art). Set once the fly leg (phase 2)
  // launches — RipsPackClaimFly treats a non-null targetPoint as "go".
  targetPoint: PackFlyPoint | null;
};

// Exported for unit testing (see useRipsPackClaimFly.test.ts) — these are pure DOM-reading
// functions, not React state, so they're testable directly under jsdom without a hook renderer.
export function getFallbackTargetPoint(): PackFlyPoint {
  return {
    x: window.innerWidth / 2,
    y: window.innerHeight - PACK_FLY_FALLBACK_TARGET_MARGIN_PX,
  };
}

// A small nudge toward screen center during the hero pop (round 6) — reinforces the "star of the
// screen" staging without actually relocating the clone. Capped at PACK_FLY_HERO_DRIFT_PX so a pack
// that's already near center doesn't overshoot past it.
export function getHeroDrift(centerX: number, centerY: number): PackFlyPoint {
  const dx = window.innerWidth / 2 - centerX;
  const dy = window.innerHeight / 2 - centerY;
  const distance = Math.hypot(dx, dy);
  if (distance === 0) {
    return { x: 0, y: 0 };
  }
  const magnitude = Math.min(PACK_FLY_HERO_DRIFT_PX, distance);
  return { x: (dx / distance) * magnitude, y: (dy / distance) * magnitude };
}

export function isRectOnScreen(rect: PackFlyRect) {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

// The rendered <img> box can be bigger than the visible art: RipsPackRewardComponent sizes it to
// 100%/100% of its layout slot and uses `object-fit: contain` to letterbox within that box, so the
// box's aspect ratio rarely matches the art's. Cloning the raw box (with no object-fit) stretches
// the clone to fill it — a visibly "fat"/distorted pack. Fit the natural image dimensions into the
// box the same way `object-fit: contain` would, so the clone's geometry hugs the actual art.
export function getContainedRect(box: PackFlyRect, naturalWidth: number, naturalHeight: number): PackFlyRect {
  if (!naturalWidth || !naturalHeight || !box.width || !box.height) {
    return box;
  }

  const boxRatio = box.width / box.height;
  const artRatio = naturalWidth / naturalHeight;

  const width = artRatio > boxRatio ? box.width : box.height * artRatio;
  const height = artRatio > boxRatio ? box.width / artRatio : box.height;

  const left = box.left + (box.width - width) / 2;
  const top = box.top + (box.height - height) / 2;

  return { left, top, width, height, right: left + width, bottom: top + height };
}

function getCrownRipsWidgetRect(): PackFlyRect | null {
  const targetEl = document.querySelector(`[data-testid="${CROWN_RIPS_WIDGET_TEST_ID}"]`);
  if (targetEl instanceof HTMLElement) {
    const rect = targetEl.getBoundingClientRect();
    if (isRectOnScreen(rect)) {
      return rect;
    }
  }
  return null;
}

// Measured fresh at flight-start time (not at claim time) — the lobby can reflow while the modal's
// close animation plays, so a target measured earlier could be stale by the time the flight starts.
export function getTargetPoint(): PackFlyPoint {
  const rect = getCrownRipsWidgetRect();
  if (rect) {
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  return getFallbackTargetPoint();
}

// Whether the fly leg actually had a real widget to land on (vs. the bottom-center fallback) —
// checked at arrival time by RipsPackClaimFlyHost to decide whether the widget's own arrival pulse
// should fire. Re-measures rather than remembering the launch-time result: the lobby can reflow
// again during the ~0.6s flight, and a pulse should only ever land on a widget that's really there.
export function isCrownRipsWidgetVisible(): boolean {
  try {
    return getCrownRipsWidgetRect() !== null;
  } catch {
    return false;
  }
}

// Build a fly request from a rendered pack <img> — the shared core of both capture paths (the
// claim dialog's art, and the claim-celebration tile). Never throws; returns null on any reason
// not to fly (unrendered/zero-size source), which callers treat as "no flourish".
//
// Deliberately does NOT check prefers-reduced-motion: nothing else in the app honors it (no
// MotionConfig — the coin celebration etc. always animate), so bailing here alone doesn't reduce
// motion, it just makes the pack silently never fly on reduce-motion machines (QA outage,
// 2026-07-21). If the app ever honors it for real, do it app-wide.
export function captureImgFlySource(
  img: HTMLImageElement,
  imageUrl: string | undefined,
): RipsPackClaimFlyRequest | null {
  try {
    if (!imageUrl) {
      return null;
    }

    const box = img.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) {
      return null;
    }

    return { imageUrl, sourceRect: getContainedRect(box, img.naturalWidth, img.naturalHeight) };
  } catch {
    return null;
  }
}

// Called synchronously from the claim dialog's onClaimHandler, while the dialog (and the rendered
// pack <img>) is still fully mounted — the dialog is about to unmount, so this is the only moment
// the source element can be measured.
export function captureRipsPackFlySource(imageUrl: string | undefined): RipsPackClaimFlyRequest | null {
  const sourceEl = document.querySelector(`[data-testid="${RIPS_PACK_REWARD_IMAGE_TEST_ID}"]`);
  if (!(sourceEl instanceof HTMLImageElement)) {
    return null;
  }
  return captureImgFlySource(sourceEl, imageUrl);
}

/**
 * Drives the two-phase claim celebration for gifts that include a ripsPack, from a component that
 * lives OUTSIDE the claim dialog's lifecycle (see RipsPackClaimFlyHost) — the dialog itself
 * unmounts mid-celebration (it's swapped for `null` the moment EventTriggerStore clears
 * `uiStore.openDialog`), so any state/timers owned by the dialog would be destroyed along with it.
 *
 * 1. `hold(request)` — call once EventTriggerStore's `ripsPackClaimFlyRequest` is set (i.e. the
 *    claim dialog called `captureRipsPackFlySource` + `requestRipsPackClaimFly`). Renders the clone
 *    in place holding — RipsPackClaimFly plays a quick scale-pop and then holds there (reads as
 *    "claimed!").
 * 2. `launchFly()` — call once the claim dialog is confirmed gone (`uiStore.openDialog.dialog`
 *    cleared), so the takeoff reads against the lobby, not the still-closing modal. Measures the
 *    Crown Rips widget's position fresh at this point (the lobby may have reflowed while the modal
 *    closed) and starts the translate/shrink/fade leg.
 *
 * A hold-cap timer (`PACK_FLY_HOLD_CAP_MS`) force-launches the fly leg if the "dialog closed"
 * signal never arrives, and a fly-phase safety timer force-finishes if `onAnimationComplete` never
 * fires — so a flight can never get permanently stuck in either phase.
 */
export function useRipsPackClaimFly() {
  const [flyState, setFlyState] = useState<PackFlyState | null>(null);
  const holdCapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flySafetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (holdCapTimeoutRef.current) clearTimeout(holdCapTimeoutRef.current);
    if (flySafetyTimeoutRef.current) clearTimeout(flySafetyTimeoutRef.current);
    holdCapTimeoutRef.current = null;
    flySafetyTimeoutRef.current = null;
  }, []);

  const finish = useCallback(() => {
    clearTimers();
    setFlyState(null);
  }, [clearTimers]);

  const launchFly = useCallback(() => {
    setFlyState((current) => {
      if (!current || current.targetPoint) {
        // Nothing holding, or already flying — launching twice would just reset the same target.
        return current;
      }

      if (holdCapTimeoutRef.current) {
        clearTimeout(holdCapTimeoutRef.current);
        holdCapTimeoutRef.current = null;
      }

      let targetPoint: PackFlyPoint;
      try {
        targetPoint = getTargetPoint();
      } catch {
        targetPoint = getFallbackTargetPoint();
      }

      flySafetyTimeoutRef.current = setTimeout(finish, FLY_SAFETY_TIMEOUT_MS);

      return { ...current, targetPoint };
    });
  }, [finish]);

  const hold = useCallback(
    (request: RipsPackClaimFlyRequest) => {
      // A still-pending timer from an unfinished prior flight (e.g. back-to-back claims faster than
      // the ~2.6s worst-case flight) must not fire against this new flight — it would finish() it
      // almost immediately. Clear before arming this flight's own timer.
      clearTimers();
      setFlyState({ ...request, targetPoint: null });
      holdCapTimeoutRef.current = setTimeout(launchFly, PACK_FLY_HOLD_CAP_MS);
    },
    [clearTimers, launchFly],
  );

  return { flyState, hold, launchFly, finish };
}
